import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  public override render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="sf-card py-8 px-4 sm:px-10">
              <div className="text-center">
                <AlertTriangle className="mx-auto h-12 w-12 text-neon-pink mb-4" />
                <h2 className="text-lg font-bold text-fg-bright mb-2">
                  Er ging iets mis
                </h2>
                <p className="text-sm text-fg-muted mb-6">
                  Sorry, er is iets onverwachts gebeurd. Probeer het opnieuw.
                </p>

                {/* Error details in development */}
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <div className="mb-6 p-4 bg-neon-pink/[.08] border border-neon-pink/30 rounded-xl text-left">
                    <h3 className="text-sm font-semibold text-neon-pink-soft mb-2">
                      Error Details:
                    </h3>
                    <pre className="text-xs text-neon-pink-soft whitespace-pre-wrap break-words">
                      {this.state.error.message}
                      {this.state.errorInfo?.componentStack && (
                        <>
                          {'\n\nComponent Stack:'}
                          {this.state.errorInfo.componentStack}
                        </>
                      )}
                    </pre>
                  </div>
                )}

                <div className="space-y-3">
                  <button
                    onClick={this.handleRetry}
                    className="sf-btn-primary w-full py-2.5 px-4 text-sm"
                  >
                    Probeer opnieuw
                  </button>

                  <button
                    onClick={this.handleReload}
                    className="sf-btn-secondary w-full py-2.5 px-4 text-sm"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Pagina herladen
                  </button>
                </div>

                <div className="mt-6 text-xs text-fg-meta">
                  Blijft dit gebeuren? Neem contact met ons op.
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;